import { useEffect, useState } from 'react';

/**
 * Holds a value back until it has stopped changing for `delayMs`.
 *
 * The catalogue search box is the reason this exists: every keystroke would otherwise be a
 * request, and an operator typing "arroz agulhinha" would spend five round trips of the p95
 * budget (RNF04) on answers nobody reads. The input itself stays uncontrolled-fast — only the
 * value the query key is built from is delayed, so typing never feels laggy.
 */
export function useDebouncedValue<TValue>(value: TValue, delayMs: number): TValue {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (delayMs <= 0) {
      setDebounced(value);
      return;
    }

    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);

    // Clearing on every change is what makes it a debounce rather than a throttle: a keystroke
    // inside the window restarts the wait instead of letting the previous one fire.
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);

  return debounced;
}
